import {PlusCircleOutlined} from '@ant-design/icons';
import {
    Button,
    Form,
} from 'antd';
import useForm from 'antd/es/form/hooks/useForm';
import cn from 'classnames';
import React, {
    FC,
    useEffect,
    useState,
} from 'react';

import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {SimpleActionButton} from 'components/form/components';
import {UploaderList} from 'components/form/inputs/uploader-list';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {RichTextEditor} from 'components/rich-text';
import {
    IAttachmentCommentFormData,
    ITableReportAttachment,
} from 'components/table-report/components/table-report-actions/actions/attachment-button/attachment-button-types';
import {
    CommentField,
} from 'components/table-report/components/table-report-actions/actions/attachment-button/comment-field/comment-field';
import {ECommonMessages} from 'shared/constants/messages';
import {useUserRoleFunctionsByDocId} from 'shared/hooks/use-user-role-functions';
import {IFileDto, TFileList} from 'shared/types/files';
import {showMessage} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {selectUserInfo} from 'store/slices/auth-slice';
import {tableReportCommentAttachmentsApi} from 'store/slices/table-report-slice/table-report-attachments';
import {
    ITableReportDocumentCommentDto,
} from 'store/slices/table-report-slice/table-report-attachments/table-report-attachments-types';
import {TTableReportExtendedConfiguration} from 'store/slices/table-report-slice/table-report-slice-types';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {generateAvatarColor} from './comment-field/comment-field.utils';

import './attachment-button.less';

interface IAttachmentButtonProps {
    docId?: string | null;
    templateConfig?: TTableReportExtendedConfiguration;
}
export const AttachmentButton: FC<IAttachmentButtonProps> = ({
    docId,
    templateConfig,
}: IAttachmentButtonProps) => {
    const [fileList, setFileList] = useState<TFileList[]>([]);
    const [deleteFileList, setDeleteFileList] = useState<string[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCommentFieldVisible, setIsCommentFieldVisible] = useState(false);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [comments, setComments] = useState<ITableReportDocumentCommentDto[]>([]);
    const [editComment, setEditComment] = useState<ITableReportDocumentCommentDto>();
    const [commentsOnDelete, setCommentsOnDelete] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [form] = useForm();
    const {downloadFile, getCommentAttachments, postCommentAttachments} = tableReportCommentAttachmentsApi;
    const user = useAppSelector(selectUserInfo);
    const {
        templateCode,
    } = templateConfig ?? {};
    const {hasUserRoleFunction: hasUserRoleFunctionWithDocId} = useUserRoleFunctionsByDocId({
        tableReportDocId: docId || null,
        templateCode: templateCode || '',
    });
    const saveEnabled = [
        EUserRoleFunction.ATTACH_FILE_TO_REPORT,
        EUserRoleFunction.DELETE_FILE_IN_REPORT,
        EUserRoleFunction.ADD_COMMENT_TO_REPORT,
        EUserRoleFunction.DELETE_COMMENT_IN_REPORT,
    ].some(func => hasUserRoleFunctionWithDocId(func));

    const isFileListVisible = !!(fileList.length && (fileList.length > deleteFileList.length));

    const handleCommentEdit = (commentData: ITableReportDocumentCommentDto) => {
        setIsCommentFieldVisible(true);
        form.setFieldsValue({comment: commentData.comment});
        setEditComment(commentData);
    };

    const handleCommentDelete = (deleteId: number) => {
        setCommentsOnDelete(oldComments => [...oldComments, deleteId]);
        setComments(oldComments => oldComments.filter(comment => comment?.id !== deleteId));
    };

    const handleClose = () => {
        setCommentsOnDelete([]);
        setDeleteFileList([]);
        setFileList([]);
        setComments([]);
        setEditComment(undefined);
        form.resetFields();
        setIsCommentFieldVisible(false);
        setIsModalOpen(false);
        setIsConfirmVisible(false);
        setIsLoading(false);
    };

    const handleFinish = (values: IAttachmentCommentFormData) => {
        setIsLoading(true);
        const newComment = [
            {
                ...(editComment && {id: editComment?.id}),
                comment: values?.comment,
                userName: user?.userName,
                fullName: user?.fullName,
                userId: user?.userId,
            },
        ];
        postCommentAttachments({
            docId,
            templateCode,
            commentsOnDelete,
            filesOnDelete: deleteFileList,
            comments: newComment,
            files: values?.files || [],
        }).then(() => {
            showMessage({message: ECommonMessages.SAVE_SUCCESS});
            handleClose();
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const handleCancel = () => {
        if (isLoading) return;
        const newFiles = fileList.filter(file => !(file as IFileDto).status);
        const areAllNewFilesDeleted = !newFiles.some(
            file => (file as IFileDto).removedFile,
        );
        const isFileListDirty = (areAllNewFilesDeleted && !deleteFileList.length && !!newFiles.length);
        if (form.isFieldsTouched(['comment']) || !!commentsOnDelete.length || isFileListDirty) {
            setIsConfirmVisible(true);
            return;
        }
        handleClose();
    };

    useEffect(() => {
        if (isModalOpen && templateCode && docId) {
            setIsLoading(true);
            getCommentAttachments({
                docId,
                templateCode,
            }).then(r => {
                const {comments: responseComments, attachments} = r?.data ?? {};
                const processedAttachments = attachments?.map((attachment: ITableReportAttachment) => ({
                    ...attachment,
                    status: attachment.outputFileSize ? 'done' : attachment.status,
                    uid: attachment.attachmentId,
                }));
                if (responseComments) setComments(responseComments);
                if (processedAttachments) setFileList(processedAttachments);
            }).catch(e => {
                showMessage({message: e.message, isError: true});
            }).finally(() => {
                setIsLoading(false);
            });
        }
    }, [templateCode, isModalOpen]);

    const confirmMessageModal = (
        <ConfirmModal
            title="Подтверждение"
            visible={isConfirmVisible}
            content={'У вас есть несохраненные изменения, при закрытии формы изменения не будут сохранены.\n'
                + ' Закрыть форму?'}
            onCancel={() => setIsConfirmVisible(false)}
            cancelText="Нет"
            onConfirm={handleClose}
            approveText="Да"
        />
    );

    return (
        <div className={cn('attachment')}>
            {isConfirmVisible && confirmMessageModal}
            <ModalOpenerComponent
                isLoading={isLoading}
                {...(saveEnabled && {
                    handleSave: () => {
                        form.submit();
                    },
                })}
                isOpen={isModalOpen}
                modalProps={{
                    centered: true,
                    title: 'Комментарии и вложения',
                    destroyOnClose: true,
                    forceRender: false,
                }}
                disabledControls={{
                    save: isLoading,
                    cancel: isLoading,
                }}
                handleCancel={handleCancel}
                componentWrapperClassNames="d-inline-flex align-items-center"
                component={(
                    <SimpleActionButton
                        className="attachment-button"
                        type="default"
                        onClick={() => {
                            setIsModalOpen(v => !v);
                        }}
                        title=""
                        icon="File"
                    />
                )}
            >
                <Form
                    className="attachment-form"
                    colon={false}
                    form={form}
                    onFinish={handleFinish}
                >
                    {!!comments.length && (
                        <div className="attachment-form__comments">
                            {comments.map((comment, index) => (
                                <CommentField
                                    key={comment.id}
                                    avatarColor={generateAvatarColor(index)}
                                    commentData={comment}
                                    handleEdit={handleCommentEdit}
                                    handleDelete={handleCommentDelete}
                                    isDeleteDisabled={
                                        !(hasUserRoleFunctionWithDocId(EUserRoleFunction.DELETE_COMMENT_IN_REPORT)
                                        && user?.userId === comment.userId)
                                    }
                                    isEditDisabled={
                                        !(hasUserRoleFunctionWithDocId(EUserRoleFunction.ADD_COMMENT_TO_REPORT)
                                        && user?.userId === comment.userId)
                                    }
                                />
                            ))}
                            {!isCommentFieldVisible && (
                                <Button
                                    className="attachment-form__comments__add-button"
                                    type="default"
                                    onClick={() => setIsCommentFieldVisible(visible => !visible)}
                                >
                                    <PlusCircleOutlined />
                                    Добавить комментарий
                                </Button>
                            )}
                        </div>
                    )}

                    {(!comments.length || isCommentFieldVisible) && (
                        <Form.Item
                            name="comment"
                            label="Оставить комментарий"
                        >
                            <RichTextEditor
                                key="comment"
                                placeholder="Введите комментарий"
                                disabled={!hasUserRoleFunctionWithDocId(EUserRoleFunction.ADD_COMMENT_TO_REPORT)}
                            />
                        </Form.Item>
                    )}
                    {!isFileListVisible
                        ? <div>К отчету не приложено ни одного файла</div>
                        : (
                            <div
                                style={{marginBottom: 0}}
                                className={cn('attachment-form__files-label')}
                            >
                                <span>Файлы</span>
                            </div>
                        )}
                    {(hasUserRoleFunctionWithDocId(EUserRoleFunction.ATTACH_FILE_TO_REPORT)
                    || hasUserRoleFunctionWithDocId(EUserRoleFunction.VIEW_FILE_AND_COMMENT_IN_REPORT))
                        && (isFileListVisible || !fileList.length)
                        && (
                            <Form.Item
                                name="files"
                            >
                                <UploaderList
                                    buttonText="Выбрать файлы"
                                    dropzoneVariant="dropzone"
                                    fileList={fileList}
                                    onChange={files => {
                                        setFileList(files);
                                        setDeleteFileList(
                                            files.filter(file => (file as IFileDto).removedFile).map(
                                                (file: IFileDto) => file.attachmentId || file.uid,
                                            ),
                                        );
                                    }}
                                    onDownload={file => {
                                        if (file?.fileName && templateCode) {
                                            downloadFile({
                                                attachmentId: file.uid,
                                                templateCode,
                                                fileName: file.fileName,
                                            });
                                        }
                                    }}
                                    styles={{
                                        buttonStyle: {
                                            marginBottom: 6,
                                        },
                                    }}
                                    disabled={!hasUserRoleFunctionWithDocId(EUserRoleFunction.ATTACH_FILE_TO_REPORT)}
                                    isDeleteDisabled={
                                        !hasUserRoleFunctionWithDocId(EUserRoleFunction.DELETE_FILE_IN_REPORT)
                                    }
                                />
                            </Form.Item>
                        )}
                </Form>
            </ModalOpenerComponent>
        </div>
    );
};
